import clsx from "clsx";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineTune } from "react-icons/md";
import { EButtonKind } from "src/components/Button/Button.types";
import InfoBlock from "src/components/InfoBlock/InfoBlock";
import { ICON_SIZES } from "src/constants/icons";
import { ETransactionType } from "src/helpers/constants";
import useMobile from "src/hooks/useMobile";
import useModalsStore from "src/store/modalsStore";
import useUiPreferences from "src/store/useUiPreferences";
import { ITransactionStatusMessage } from "./TransactionStatusMessage.types";
import { EInfoBlockKind } from "src/components/InfoBlock/InfoBlock.types";

const TransactionStatusMessage: React.FC<ITransactionStatusMessage> = ({
  funds,
  amount,
  showTitle = true,
  transactionType,
  showButtons = true,
}) => {
  const accountsUiPreferences = useUiPreferences((s) => s.accounts);

  const { setShowAccountsSettingsModal } = useModalsStore();
  const { isMobile } = useMobile();
  const { t } = useTranslation();

  const hasFunds = useMemo(() => {
    if (transactionType === ETransactionType.INCOME) {
      return true;
    }
    return funds > 0;
  }, [funds, amount]);

  const amountIsGreaterThanFunds = useMemo(() => {
    return Number(amount) > funds;
  }, [amount, funds]);

  const noFundsMessage = useMemo(() => {
    if (accountsUiPreferences.allowTransfersWithNoFunds) {
      return `${t("modals:transfer_funds.no_enough_funds")} ${t("modals:transfer_funds.transfer_with_no_funds_allowed")}`;
    }

    return t("modals:transfer_funds.no_enough_funds");
  }, [accountsUiPreferences.allowTransfersWithNoFunds]);

  const amountsIsGreaterThanAvailableMessage = useMemo(() => {
    if (accountsUiPreferences.allowTransfersWithNoFunds) {
      return `${t("modals:transfer_funds.amount_is_higher")} ${t("modals:transfer_funds.transfer_with_no_funds_allowed")}`;
    }

    return t("modals:transfer_funds.amount_is_higher");
  }, [accountsUiPreferences.allowTransfersWithNoFunds]);

  if (transactionType === ETransactionType.INCOME) return null;

  if (hasFunds && !amountIsGreaterThanFunds) return null;

  return (
    <div className={clsx(["flex", "w-full", "flex-col", "gap-2"])}>
      {!hasFunds && (
        <InfoBlock
          message={noFundsMessage}
          displayMobileIcon
          iconPosition="center"
          kind={
            accountsUiPreferences.allowTransfersWithNoFunds
              ? EInfoBlockKind.WARNING
              : EInfoBlockKind.ERROR
          }
          // icon={<MdWarning />}
          title={
            showTitle
              ? accountsUiPreferences.allowTransfersWithNoFunds
                ? "Cuenta sin fondos"
                : "Cuenta sin fondos"
              : undefined
          }
          buttons={
            showButtons
              ? [
                  {
                    tooltip: isMobile ? "" : t("Opciones"),
                    onClick: () => {
                      setShowAccountsSettingsModal(true);
                    },
                    icon: <MdOutlineTune size={ICON_SIZES.SM} />,
                    kind: accountsUiPreferences.allowTransfersWithNoFunds
                      ? EButtonKind.EDIT
                      : EButtonKind.DANGER,
                  },
                ]
              : []
          }
        />
      )}
      {hasFunds && amountIsGreaterThanFunds && (
        <InfoBlock
          message={amountsIsGreaterThanAvailableMessage}
          displayMobileIcon
          iconPosition="center"
          kind={
            accountsUiPreferences.allowTransfersWithNoFunds
              ? EInfoBlockKind.WARNING
              : EInfoBlockKind.ERROR
          }
          // icon={<MdWarning />}
          title={
            showTitle
              ? accountsUiPreferences.allowTransfersWithNoFunds
                ? "Fondos insuficientes"
                : "Fondos insuficientes"
              : undefined
          }
          buttons={
            showButtons
              ? [
                  {
                    tooltip: isMobile ? "" : t("Opciones"),
                    onClick: () => {
                      setShowAccountsSettingsModal(true);
                    },
                    icon: <MdOutlineTune size={ICON_SIZES.SM} />,
                    kind: accountsUiPreferences.allowTransfersWithNoFunds
                      ? EButtonKind.EDIT
                      : EButtonKind.DANGER,
                  },
                ]
              : []
          }
        />
      )}
    </div>
  );
};

export default memo(TransactionStatusMessage);
